import { useState } from 'react';
import { DeleteButton, ErrorBanner, Field, Loading, Modal } from '../../components/ui';
import { api } from '../../lib/api';
import { useResource } from '../../lib/hooks';
import type { Comparison, EosTemplate, Unit } from './eosTypes';
import { EosSection, formatMeasure } from './eosUi';

interface DraftItem {
  name: string;
  unit: Unit;
  goal: string;
  comparison: Comparison;
}

const COMPARISON_LABELS: Record<Comparison, string> = {
  '>=': 'at least',
  '<=': 'at most',
  '=': 'exactly',
};

/**
 * Scorecard templates — a reusable set of measurables.
 *
 * Admin-only, because a template is shared across every team: editing one
 * changes what every future scorecard starts from. The two ways in are
 * building one by hand and snapshotting a team's current scorecard, and the
 * second is the one that actually gets used — a team refines their measurables
 * for a quarter and then wants the next team to start from that rather than
 * from nothing.
 *
 * Applying a template APPENDS. It does not replace what a team already has,
 * because replacing would cascade away their score history — see the note on
 * the apply endpoint in `routes/eos.ts`.
 */
export function EosTemplates({ teamId }: { teamId: number }) {
  const templates = useResource<EosTemplate[]>('/eos/templates');
  const [editing, setEditing] = useState<EosTemplate | null>(null);
  const [creating, setCreating] = useState(false);
  const [snapshotName, setSnapshotName] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const apply = async (template: EosTemplate) => {
    setError(null);
    setNotice(null);
    try {
      const result = await api.post<{ added: number }>(`/eos/templates/${template.TemplateId}/apply`, {
        teamId,
      });
      setNotice(
        `Added ${result.added} ${result.added === 1 ? 'measurable' : 'measurables'} from "${template.Name}" to this team's scorecard.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not apply that template.');
    }
  };

  const snapshot = async () => {
    if (!snapshotName.trim()) return;
    setError(null);
    setNotice(null);
    try {
      await api.post('/eos/templates/from-scorecard', { teamId, name: snapshotName.trim() });
      setSnapshotName('');
      templates.reload();
      setNotice('Saved this team’s current scorecard as a template.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save that snapshot.');
    }
  };

  return (
    <>
      {error && <ErrorBanner message={error} />}
      {notice && <div className="alert" role="status">{notice}</div>}

      <EosSection
        title="Scorecard templates"
        note="Reusable measurable sets. Applying one adds its measurables to the selected team — it does not replace what is already there."
        actions={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setCreating(true)}>
            New template
          </button>
        }
      >
        {templates.loading && <Loading label="Loading templates" />}
        {templates.error && <ErrorBanner message={templates.error} />}
        {templates.data?.length === 0 && !templates.loading && (
          <p className="muted">
            No templates yet. Build one, or snapshot a team's scorecard below once it looks right.
          </p>
        )}

        {/* A grid of cards rather than a stacked list: a template is a small
            self-contained thing you compare against its neighbours, and seven of
            them full-width is a lot of scrolling to see two. Its measurables are
            listed on the face of the card, name against goal, so choosing one
            does not mean opening each in turn. */}
        <div className="eos-tpl-grid">
          {templates.data?.map((template) => (
            <div key={template.TemplateId} className="card">
              <div className="card-body">
                <div className="strong">{template.Name}</div>
                <div className="xs subtle">
                  {template.items.length} {template.items.length === 1 ? 'measurable' : 'measurables'}
                  {template.Description && ` · ${template.Description}`}
                </div>

                {template.items.length > 0 && (
                  <ul className="eos-tpl-items">
                    {template.items.map((item) => (
                      <li key={item.TemplateMeasurableId}>
                        <span>{item.Name}</span>
                        <span>
                          {COMPARISON_LABELS[item.Comparison]}{' '}
                          {formatMeasure(Number(item.Goal), item.Unit)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                <div className="row wrap" style={{ gap: 4 }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => void apply(template)}
                  >
                    Apply to this team
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => setEditing(template)}
                  >
                    Edit
                  </button>
                  <DeleteButton
                    what={`the template "${template.Name}"`}
                    onConfirm={() => {
                      void api
                        .del(`/eos/templates/${template.TemplateId}`)
                        .then(() => templates.reload())
                        .catch((err: unknown) =>
                          setError(err instanceof Error ? err.message : 'Could not delete it.'),
                        );
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </EosSection>

      <EosSection
        title="Snapshot the current scorecard"
        note="Turns the selected team's active measurables into a new template. Owners are not carried over — they belong to the team, not the template."
      >
        <form
          className="row wrap"
          style={{ gap: 8, alignItems: 'flex-end' }}
          onSubmit={(e) => {
            e.preventDefault();
            void snapshot();
          }}
        >
          <div className="grow">
            <Field label="Template name">
              <input
                className="input"
                placeholder="e.g. Service desk weekly"
                value={snapshotName}
                onChange={(e) => setSnapshotName(e.target.value)}
              />
            </Field>
          </div>
          <button type="submit" className="btn btn-primary" disabled={!snapshotName.trim()}>
            Snapshot
          </button>
        </form>
      </EosSection>

      {(creating || editing) && (
        <TemplateForm
          template={editing}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            templates.reload();
          }}
        />
      )}
    </>
  );
}

function TemplateForm({
  template,
  onClose,
  onSaved,
}: {
  template: EosTemplate | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(template?.Name ?? '');
  const [description, setDescription] = useState(template?.Description ?? '');
  const [items, setItems] = useState<DraftItem[]>(
    template?.items.map((i) => ({
      name: i.Name,
      unit: i.Unit,
      goal: String(Number(i.Goal)),
      comparison: i.Comparison,
    })) ?? [],
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const setItem = (index: number, patch: Partial<DraftItem>) =>
    setItems((current) => current.map((item, i) => (i === index ? { ...item, ...patch } : item)));

  const submit = async () => {
    if (!name.trim()) {
      setError('A template needs a name.');
      return;
    }
    /* Blank rows are dropped rather than rejected: adding a row and changing
       your mind is normal, and making somebody delete it before saving is
       friction for nothing. */
    const cleaned = items.filter((i) => i.name.trim() !== '');
    if (cleaned.some((i) => Number.isNaN(Number(i.goal)))) {
      setError('Every goal has to be a number.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        items: cleaned.map((i) => ({
          name: i.name.trim(),
          unit: i.unit,
          goal: Number(i.goal),
          comparison: i.comparison,
        })),
      };
      if (template) await api.put(`/eos/templates/${template.TemplateId}`, body);
      else await api.post('/eos/templates', body);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save.');
      setSaving(false);
    }
  };

  return (
    <Modal
      title={template ? 'Edit template' : 'New template'}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void submit()}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <ErrorBanner message={error} />}
      <div className="form-grid">
        <Field label="Name">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <input
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Field>
      </div>

      <div className="divider mt-4" />
      <div className="xs subtle">Measurables</div>

      {items.map((item, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={index} className="row wrap mt-2" style={{ gap: 8, alignItems: 'flex-end' }}>
          <div className="grow">
            <input
              className="input"
              placeholder="Measurable"
              value={item.name}
              onChange={(e) => setItem(index, { name: e.target.value })}
            />
          </div>
          <select
            className="select"
            value={item.comparison}
            aria-label="Goal rule"
            onChange={(e) => setItem(index, { comparison: e.target.value as Comparison })}
          >
            <option value=">=">At least</option>
            <option value="<=">At most</option>
            <option value="=">Exactly</option>
          </select>
          <input
            className="input tabular"
            style={{ maxWidth: 120 }}
            inputMode="decimal"
            aria-label="Goal"
            value={item.goal}
            onChange={(e) => setItem(index, { goal: e.target.value })}
          />
          <select
            className="select"
            value={item.unit}
            aria-label="Unit"
            onChange={(e) => setItem(index, { unit: e.target.value as Unit })}
          >
            <option value="#">#</option>
            <option value="$">$</option>
            <option value="%">%</option>
          </select>
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            aria-label={`Remove ${item.name || 'this measurable'}`}
            onClick={() => setItems((current) => current.filter((_, i) => i !== index))}
          >
            ✕
          </button>
        </div>
      ))}

      <button
        type="button"
        className="btn btn-secondary btn-sm mt-4"
        onClick={() =>
          setItems((current) => [...current, { name: '', unit: '#', goal: '0', comparison: '>=' }])
        }
      >
        Add measurable
      </button>
    </Modal>
  );
}
